import phoneGpsRecorderNative, { PHONE_GPS_RECORDER_STATUSES } from '@/lib/native/phoneGpsRecorder'
import {
    PHONE_GPS_RECORDING_STATUS_RECORDING,
    PHONE_GPS_RECORDING_STATUS_STOPPED,
    buildPhoneGpsRecordingFilename,
    buildPhoneGpsRecordingLocalFilePath,
    completePhoneGpsRecordingSession,
    createPhoneGpsRecordingSession,
    ensurePhoneGpsRecordingSchema,
    fetchPhoneGpsRecordingPoints,
    generateRecordingUuid,
    getPhoneGpsRecordingSessionByUuid,
    markPhoneGpsRecordingInterrupted,
    normalizePhoneGpsTimestampMs,
    updatePhoneGpsRecordingPolarMetadata,
} from '@/lib/sqlite/features/phone-gps-recording-feature'
import { hasValue, toNullableNumber } from '@/lib/helpers/numeric'

let nativeListenersReady = false
const nativeListenerHandles = []

const toPositiveId = (value) => {
    const number = toNullableNumber(value)
    return number && number > 0 ? number : null
}

const toTimestampMs = (value, fallback = null) => {
    const timestamp = normalizePhoneGpsTimestampMs(value)
    if (timestamp) return timestamp

    const fallbackTimestamp = normalizePhoneGpsTimestampMs(fallback)
    return fallbackTimestamp || null
}

const resolveUserId = (rootState) => {
    const user = rootState?.auth?.user || {}
    const userId = toPositiveId(user.id ?? user.user_id)
    if (!userId) {
        throw new Error('Authenticated user is required to start phone recording')
    }
    return userId
}

const normalizeStartPayload = (payload = {}, rootState) => ({
    recordingUuid: payload.recordingUuid || generateRecordingUuid(),
    userId: resolveUserId(rootState),
    horseId: toPositiveId(payload.horseId),
    riderId: toPositiveId(payload.riderId),
    discipline: hasValue(payload.discipline) ? String(payload.discipline) : null,
})

const normalizeNativeState = (state = {}) => {
    const startedAt = toTimestampMs(state.startedAt)
    const endedAt = toTimestampMs(state.endedAt)
    const lastPointAt = toTimestampMs(state.lastPointAt)

    return {
        recordingUuid: state.recordingUuid || null,
        status: state.status || PHONE_GPS_RECORDER_STATUSES.UNAVAILABLE,
        startedAt,
        endedAt,
        lastPointAt,
        pointsCount: Math.max(0, Number(state.pointsCount) || 0),
        totalDistanceMeters: Math.max(0, Number(state.totalDistanceMeters) || 0),
        currentSpeedMps: toNullableNumber(state.currentSpeedMps),
        filename: state.filename || null,
        manifestPath: state.manifestPath || null,
        stopReason: state.stopReason || null,
        lastErrorCode: state.lastErrorCode || state.code || null,
        lastErrorMessage: state.lastErrorMessage || state.message || null,
    }
}

const normalizeNativePoint = (point = {}, fallbackSeq = 1) => {
    const timestamp = toTimestampMs(point.timestamp ?? point.t ?? point.time)
    const latitude = toNullableNumber(point.latitude ?? point.lat ?? point.coords?.latitude)
    const longitude = toNullableNumber(point.longitude ?? point.lon ?? point.lng ?? point.coords?.longitude)

    if (!timestamp || latitude === null || longitude === null) return null

    return {
        seq: Math.max(1, Number(point.seq ?? fallbackSeq) || fallbackSeq),
        timestamp,
        latitude,
        longitude,
        accuracy: toNullableNumber(point.accuracy ?? point.coords?.accuracy),
        altitude: toNullableNumber(point.altitude ?? point.alt ?? point.coords?.altitude),
        altitudeAccuracy: toNullableNumber(
            point.altitudeAccuracy ?? point.altitude_accuracy ?? point.coords?.altitudeAccuracy
        ),
        speed: toNullableNumber(point.speed ?? point.coords?.speed),
        heading: toNullableNumber(point.heading ?? point.coords?.heading),
    }
}

const parseNativeSourcePoints = (sourceText) => {
    if (!sourceText || typeof sourceText !== 'string') return []

    try {
        const source = JSON.parse(sourceText)
        return (Array.isArray(source?.points) ? source.points : [])
            .map((point, index) => normalizeNativePoint(point, index + 1))
            .filter(Boolean)
    } catch (error) {
        console.warn('[GPS] Failed to parse native phone recording file', error)
        return []
    }
}

const buildSummary = ({ state, session = null, result = {}, source = 'native', endedAt = Date.now() }) => ({
    sessionId: session?.id ?? state.sessionId ?? null,
    recordingUuid: result.recordingUuid || session?.recordingUuid || state.recordingUuid || null,
    filename: result.filename || session?.filename || state.filename || null,
    startedAt: toTimestampMs(result.startedAt, session?.startedAt || state.startedAt),
    endedAt,
    pointsCount: Math.max(0, Number(result.pointsCount ?? session?.pointCount ?? state.pointsCount) || 0),
    totalDistanceMeters: Math.max(0, Number(result.totalDistanceMeters ?? state.totalDistanceMeters) || 0),
    currentSpeedMps: toNullableNumber(result.currentSpeedMps ?? state.currentSpeedMps),
    usePolar: !!state.usePolar,
    polarDeviceId: state.polarDeviceId || null,
    polarDeviceName: state.polarDeviceName || null,
    polarExpectedExerciseTs: state.polarExpectedExerciseTs || null,
    stopReason: result.stopReason || null,
    source,
})

export default {
    namespaced: true,
    state: () => ({
        isRecording: false,
        isNative: false,
        sessionId: null,
        recordingUuid: null,
        filename: null,
        nativeManifestPath: null,
        localFilePath: null,
        startedAt: null,
        endedAt: null,
        lastPointAt: null,
        pointsCount: 0,
        totalDistanceMeters: 0,
        currentSpeedMps: null,
        lastSessionId: null,
        lastRecordingUuid: null,
        lastSessionSummary: null,
        usePolar: false,
        polarDeviceId: null,
        polarDeviceName: null,
        polarExpectedExerciseTs: null,
    }),
    mutations: {
        start(state, payload) {
            state.isRecording = true
            state.isNative = payload.isNative === true
            state.sessionId = payload.sessionId || null
            state.recordingUuid = payload.recordingUuid || null
            state.filename = payload.filename || null
            state.nativeManifestPath = payload.nativeManifestPath || null
            state.localFilePath = payload.localFilePath || null
            state.startedAt = payload.startedAt || Date.now()
            state.endedAt = null
            state.lastPointAt = null
            state.pointsCount = Math.max(0, Number(payload.pointsCount) || 0)
            state.totalDistanceMeters = Math.max(0, Number(payload.totalDistanceMeters) || 0)
            state.currentSpeedMps = toNullableNumber(payload.currentSpeedMps)
            state.usePolar = payload.usePolar === true
            state.polarDeviceId = payload.deviceId || payload.polarDeviceId || null
            state.polarDeviceName = payload.deviceName || payload.polarDeviceName || null
            state.polarExpectedExerciseTs = toTimestampMs(payload.expectedExerciseTs ?? payload.polarExpectedExerciseTs)
        },
        setPolarRecording(state, payload = {}) {
            state.usePolar = payload.usePolar === true
            state.polarDeviceId = payload.deviceId || null
            state.polarDeviceName = payload.deviceName || null
            state.polarExpectedExerciseTs = toTimestampMs(payload.expectedExerciseTs)
        },
        recordPoint(state, payload = {}) {
            const payloadPointCount = Number(payload.pointsCount)
            state.lastPointAt = payload.timestamp || Date.now()
            state.pointsCount =
                Number.isFinite(payloadPointCount) && payloadPointCount > 0
                    ? Math.max(state.pointsCount, payloadPointCount)
                    : state.pointsCount + 1
            state.totalDistanceMeters = Math.max(
                0,
                Number(payload.totalDistanceMeters ?? state.totalDistanceMeters) || 0
            )
            state.currentSpeedMps = toNullableNumber(payload.currentSpeedMps)
        },
        updateNativeState(state, payload = {}) {
            if (payload.startedAt) state.startedAt = payload.startedAt
            if (payload.filename) state.filename = payload.filename
            if (payload.manifestPath) {
                state.nativeManifestPath = payload.manifestPath
                state.localFilePath = payload.manifestPath
            }
            state.pointsCount = Math.max(state.pointsCount, Number(payload.pointsCount) || 0)
            state.totalDistanceMeters = Math.max(
                0,
                Number(payload.totalDistanceMeters ?? state.totalDistanceMeters) || 0
            )
            state.currentSpeedMps = toNullableNumber(payload.currentSpeedMps)
            state.lastPointAt = payload.lastPointAt || state.lastPointAt
        },
        stop(state, { endedAt, summary }) {
            state.isRecording = false
            state.isNative = false
            state.endedAt = endedAt
            state.lastPointAt = null
            state.lastSessionId = summary?.sessionId ?? null
            state.lastRecordingUuid = summary?.recordingUuid ?? state.recordingUuid ?? null
            state.lastSessionSummary = summary ?? null
            state.sessionId = null
            state.recordingUuid = null
            state.filename = null
            state.nativeManifestPath = null
            state.localFilePath = null
            state.startedAt = null
            state.pointsCount = 0
            state.totalDistanceMeters = 0
            state.currentSpeedMps = null
            state.usePolar = false
            state.polarDeviceId = null
            state.polarDeviceName = null
            state.polarExpectedExerciseTs = null
        },
    },
    actions: {
        async ensureSchema() {
            await ensurePhoneGpsRecordingSchema()
        },
        async ensurePermissions() {
            if (!phoneGpsRecorderNative.isAvailable()) {
                return false
            }

            let permissions = await phoneGpsRecorderNative.checkPermissions()
            if (permissions.location !== 'granted') {
                permissions = await phoneGpsRecorderNative.requestPermissions()
            }
            return permissions.location === 'granted'
        },
        async attachNativeListeners({ dispatch }) {
            if (nativeListenersReady || !phoneGpsRecorderNative.isAvailable()) return

            await phoneGpsRecorderNative.removeAllListeners()
            nativeListenersReady = true
            nativeListenerHandles.push(
                await phoneGpsRecorderNative.addListener('gpsPoint', (point) => {
                    void dispatch('handleNativePoint', point)
                })
            )
            nativeListenerHandles.push(
                await phoneGpsRecorderNative.addListener('recordingState', (state) => {
                    void dispatch('handleNativeState', state)
                })
            )
            nativeListenerHandles.push(
                await phoneGpsRecorderNative.addListener('notificationStopRequested', (state) => {
                    void dispatch('finalizeNativeRecording', state)
                })
            )
            nativeListenerHandles.push(
                await phoneGpsRecorderNative.addListener('recordingError', (error) => {
                    console.warn('[GPS] Native phone recording error', error)
                    void dispatch('handleNativeError', error)
                })
            )
        },
        async detachNativeListeners() {
            await Promise.all(nativeListenerHandles.splice(0).map((handle) => handle?.remove?.()))
            nativeListenersReady = false
        },
        async start({ state, commit, dispatch, rootState }, payload = {}) {
            if (state.isRecording) return state.recordingUuid || state.sessionId

            if (!phoneGpsRecorderNative.isAvailable()) {
                throw new Error('Phone GPS recording is unavailable')
            }

            await ensurePhoneGpsRecordingSchema()

            const options = normalizeStartPayload(payload, rootState)
            // The program day as the ride starts stays on the phone (sqlite), with the
            // recording, for the end-of-ride gaits: never sent to the native recorder.
            const programGuidance =
                payload.programGuidance && typeof payload.programGuidance === 'object' ? payload.programGuidance : null
            await dispatch('attachNativeListeners')
            const nativeState = normalizeNativeState(await phoneGpsRecorderNative.startRecording(options))
            const startedAt = nativeState.startedAt || Date.now()
            const filename =
                nativeState.filename ||
                buildPhoneGpsRecordingFilename({
                    recordingUuid: options.recordingUuid,
                    startedAt,
                })
            const localFilePath = nativeState.manifestPath || buildPhoneGpsRecordingLocalFilePath(filename)
            const session = await createPhoneGpsRecordingSession({
                ...options,
                programGuidance,
                status: PHONE_GPS_RECORDING_STATUS_RECORDING,
                startedAt,
                filename,
                nativeManifestPath: nativeState.manifestPath,
                localFilePath,
                pointCount: nativeState.pointsCount,
            })

            commit('start', {
                isNative: true,
                sessionId: session.id,
                recordingUuid: session.recordingUuid,
                filename: session.filename,
                nativeManifestPath: nativeState.manifestPath,
                localFilePath,
                startedAt: session.startedAt,
                pointsCount: nativeState.pointsCount,
                totalDistanceMeters: nativeState.totalDistanceMeters,
                currentSpeedMps: nativeState.currentSpeedMps,
            })
            return session.recordingUuid
        },
        async setPolarRecording({ state, commit }, payload = {}) {
            commit('setPolarRecording', payload)
            if (!state.recordingUuid) return

            try {
                await updatePhoneGpsRecordingPolarMetadata(state.recordingUuid, payload)
            } catch (error) {
                console.warn('[GPS] Failed to persist phone Polar recording metadata', error)
            }
        },
        async handleNativePoint({ state, commit }, point) {
            if (!state.isRecording || !state.isNative) return

            const nativeState = normalizeNativeState(point?.state || {})
            if (nativeState.recordingUuid && nativeState.recordingUuid !== state.recordingUuid) return

            const normalizedPoint = normalizeNativePoint(point, state.pointsCount + 1)
            commit('recordPoint', {
                timestamp: nativeState.lastPointAt || normalizedPoint?.timestamp || Date.now(),
                pointsCount: nativeState.pointsCount || normalizedPoint?.seq || state.pointsCount + 1,
                totalDistanceMeters: nativeState.totalDistanceMeters || point?.totalDistanceMeters,
                currentSpeedMps: nativeState.currentSpeedMps ?? point?.currentSpeedMps ?? normalizedPoint?.speed,
                latitude: normalizedPoint?.latitude,
                longitude: normalizedPoint?.longitude,
            })
        },
        async handleNativeState({ state, commit, dispatch }, nextState) {
            const nativeState = normalizeNativeState(nextState)
            if (!nativeState.recordingUuid || nativeState.recordingUuid !== state.recordingUuid) return

            if (nativeState.status === PHONE_GPS_RECORDER_STATUSES.RECORDING) {
                commit('updateNativeState', nativeState)
                return
            }

            if (
                nativeState.status === PHONE_GPS_RECORDER_STATUSES.STOPPED ||
                nativeState.status === PHONE_GPS_RECORDER_STATUSES.INTERRUPTED
            ) {
                await dispatch('finalizeNativeRecording', nativeState)
            }
        },
        async finalizeNativeRecording({ state, commit }, result = {}) {
            const recordingUuid = result.recordingUuid || state.recordingUuid
            if (!recordingUuid) return null

            const endedAt = toTimestampMs(result.endedAt, Date.now()) || Date.now()
            const isInterrupted = result.status === PHONE_GPS_RECORDER_STATUSES.INTERRUPTED
            const session = isInterrupted
                ? await markPhoneGpsRecordingInterrupted(recordingUuid, {
                      endedAt,
                      pointCount: result.pointsCount ?? state.pointsCount,
                      nativeManifestPath: result.manifestPath ?? state.nativeManifestPath,
                      localFilePath: result.manifestPath ?? state.localFilePath,
                      code: result.lastErrorCode,
                      message: result.lastErrorMessage,
                  })
                : await completePhoneGpsRecordingSession(recordingUuid, {
                      status: PHONE_GPS_RECORDING_STATUS_STOPPED,
                      endedAt,
                      pointCount: result.pointsCount ?? state.pointsCount,
                      nativeManifestPath: result.manifestPath ?? state.nativeManifestPath,
                      localFilePath: result.manifestPath ?? state.localFilePath,
                  })
            const summary = buildSummary({
                state,
                session,
                result,
                source: 'native',
                endedAt,
            })

            if (state.isRecording && state.recordingUuid === recordingUuid) {
                commit('stop', { endedAt, summary })
            }

            return summary
        },
        async handleNativeError({ state, dispatch }, error = {}) {
            if (!state.recordingUuid) return

            const nativeState = phoneGpsRecorderNative.isAvailable()
                ? normalizeNativeState(await phoneGpsRecorderNative.getState())
                : null
            if (nativeState?.status === PHONE_GPS_RECORDER_STATUSES.INTERRUPTED) {
                await dispatch('finalizeNativeRecording', {
                    ...nativeState,
                    lastErrorCode: nativeState.lastErrorCode || error?.code || 'recording_error',
                    lastErrorMessage: nativeState.lastErrorMessage || error?.message,
                })
            }
        },
        async stop({ state, commit }) {
            if (!state.recordingUuid && !state.sessionId) return null

            const recordingUuid = state.recordingUuid
            const snapshot = { ...state }

            const result = normalizeNativeState(await phoneGpsRecorderNative.stopRecording())
            const endedAt = result.endedAt || Date.now()
            const session = await completePhoneGpsRecordingSession(recordingUuid, {
                endedAt,
                pointCount: result.pointsCount || snapshot.pointsCount,
                nativeManifestPath: result.manifestPath || snapshot.nativeManifestPath,
                localFilePath: result.manifestPath || snapshot.localFilePath,
            })
            const summary = buildSummary({
                state: snapshot,
                session,
                result,
                source: 'native',
                endedAt,
            })
            commit('stop', { endedAt, summary })
            return summary
        },
        async syncNativeState({ state, commit, dispatch }) {
            if (!phoneGpsRecorderNative.isAvailable()) return null

            await dispatch('attachNativeListeners')
            const nativeState = normalizeNativeState(await phoneGpsRecorderNative.getState())
            if (!nativeState.recordingUuid) {
                return nativeState
            }

            const session = await getPhoneGpsRecordingSessionByUuid(nativeState.recordingUuid)
            if (state.isRecording && state.recordingUuid === nativeState.recordingUuid) {
                commit('updateNativeState', nativeState)
                if (!state.usePolar && session?.usePolar) {
                    commit('setPolarRecording', {
                        usePolar: true,
                        deviceId: session.polarDeviceId,
                        deviceName: session.polarDeviceName,
                        expectedExerciseTs: session.polarExpectedExerciseTs,
                    })
                }
            } else if (session) {
                commit('start', {
                    isNative: true,
                    sessionId: session.id,
                    recordingUuid: session.recordingUuid,
                    filename: session.filename || nativeState.filename,
                    nativeManifestPath: nativeState.manifestPath || session.nativeManifestPath,
                    localFilePath: nativeState.manifestPath || session.localFilePath,
                    startedAt: nativeState.startedAt || session.startedAt,
                    pointsCount: nativeState.pointsCount || session.pointCount,
                    totalDistanceMeters: nativeState.totalDistanceMeters,
                    currentSpeedMps: nativeState.currentSpeedMps,
                    usePolar: session.usePolar,
                    polarDeviceId: session.polarDeviceId,
                    polarDeviceName: session.polarDeviceName,
                    polarExpectedExerciseTs: session.polarExpectedExerciseTs,
                })
            }

            if (
                nativeState.status === PHONE_GPS_RECORDER_STATUSES.STOPPED ||
                nativeState.status === PHONE_GPS_RECORDER_STATUSES.INTERRUPTED
            ) {
                const finalizedSummary = await dispatch('finalizeNativeRecording', nativeState)
                return { ...nativeState, finalizedSummary }
            }

            return nativeState
        },
        async exportSession({ state }, recordingUuid = null) {
            const uuid = recordingUuid || state.lastRecordingUuid || state.recordingUuid
            if (!uuid) return null

            const points = await fetchPhoneGpsRecordingPoints(uuid, {
                limit: 1000000,
                offset: 0,
            })
            if (points.length > 0) {
                return points
            }

            if (phoneGpsRecorderNative.isAvailable()) {
                const exported = await phoneGpsRecorderNative.exportRecording({ recordingUuid: uuid })
                return parseNativeSourcePoints(exported?.sourceText)
            }

            return []
        },
    },
}
